import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { usePrevious } from 'react-use';
import useTranslation from 'next-translate/useTranslation';

import { useAtomValue } from 'jotai';

import { useFindSPFromMessageId } from '@/components/pages/conversations/SocialInbox/hooks/useFindSPFromMessage';
import useComments from '@/utils/hooks/swr/useComments';
import useMentions from '@/utils/hooks/swr/useMentions';

import type { MessageType } from '@/types/messageType';
import type { MentionType } from '@/components/pages/conversations/SocialInbox/types';

import { ALL } from '@/lib/manage/mentions';
import { CONVERSATIONS_REFRESH_INTERVAL } from '@/components/pages/conversations/SocialInbox/const';

import { openedMessageAtom } from '@/components/pages/conversations/SocialInbox/atoms';

import { EmptyState } from '@/components/base/EmptyState';
import CommentsList from '@/components/composite/Comments/CommentsList';
import { SocialInboxForm } from '@/components/pages/conversations/SocialInbox';
import MentionsList from '@/components/pages/manage/mentionsTracker/MentionsList';

export const SocialInboxConversationMessages = () => {
    const { t } = useTranslation('conversations');
    const openedMessage = useAtomValue(openedMessageAtom) as MessageType;
    const formMethods = useForm({ mode: 'all', defaultValues: { message: '' } });
    const isMention = 'mentioned_in' in openedMessage;

    const {
        isLoadingInitialData: isLoadingMentions,
        isLoadingMore: isLoadingMoreMentions,
        isMoreToBeLoaded: isMoreToBeLoadedMentions,
        loadMore: loadMoreMentions,
        mentions,
        mutate: mutateMentions
    } = useMentions({
        activeSPID: openedMessage?.social_profile_id,
        limit: 10,
        options: { refreshInterval: CONVERSATIONS_REFRESH_INTERVAL },
        shouldFetch: isMention
    });
    const {
        dataComments,
        isLoadingInitialData: isLoadingComments,
        isLoadMore: isLoadMoreComments,
        isLoadingMore: isLoadingMoreComments,
        commentsError,
        mutateComments,
        loadMoreComments
    } = useComments({
        activeSPID: openedMessage?.social_profile_id,
        mediaId: openedMessage?.media_id,
        shouldFetch: !isMention
        //  TODO: fix type when refactoring useComments to use TS
    } as any);
    const { socialProfile } = useFindSPFromMessageId({ message: openedMessage });
    const previousOpenedMessage = usePrevious(openedMessage);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const comments = dataComments?.flatMap((page) => page.data) ?? [];

    useEffect(() => {
        if (previousOpenedMessage?.id !== openedMessage?.id) {
            formMethods.resetField('message');
        }
         
    }, [openedMessage]);

    const scrollToBottom = () => {
        if (scrollContainerRef.current) {
            // Use requestAnimationFrame to ensure DOM is updated
            requestAnimationFrame(() => {
                const container = scrollContainerRef.current;
                if (container) {
                    // Force a reflow to ensure content is rendered
                    container.scrollTop = container.scrollHeight;
                    // Double check after a small delay
                    setTimeout(() => {
                        container.scrollTop = container.scrollHeight;
                    }, 100);
                }
            });
        }
    };

    useEffect(() => {
        // Add a small delay to ensure content is rendered
        const timer = setTimeout(() => {
            scrollToBottom();
        }, 100);

        return () => clearTimeout(timer);
    }, [isLoadingComments, isLoadingMoreComments, isLoadingMoreMentions, isLoadingMentions]);

    if (!openedMessage) return null;

    if (commentsError) {
        return (
            <EmptyState
                icon="alert"
                title={t('social_inbox.conversation_error_title')}
                description={t('social_inbox.conversation_error_description')}
                className="h-full"
            />
        );
    }

    const handleReply = (args: { currentComment: MentionType | MessageType }) => {
        if (args.currentComment?.data?.username) {
            formMethods.setValue('message', `@${args.currentComment.data.username} `);
        } else {
            formMethods.setValue('message', '');
        }
        formMethods.setFocus('message');
    };

    return (
        <FormProvider {...formMethods}>
            <div
                ref={scrollContainerRef}
                className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 [overflow-anchor:none] sm:px-10">
                {isMention ? (
                    <MentionsList
                        isLoadingMore={isLoadingMoreMentions}
                        loadMore={loadMoreMentions}
                        isMoreToBeLoaded={isMoreToBeLoadedMentions}
                        mentions={[mentions.find((mention) => mention.id === openedMessage.id)]}
                        mentionType={ALL}
                        mutateMentions={mutateMentions}
                        unreadCount={0}
                        onReply={handleReply}
                    />
                ) : (
                    <CommentsList
                        className="mt-auto flex flex-col justify-end"
                        dataComments={[
                            {
                                data: comments.filter((message) => message.id === openedMessage.id),
                                summary: {
                                    total_count: 1,
                                    unread_count: 0
                                }
                            }
                        ]}
                        socialProfile={socialProfile}
                        isLoading={isLoadingComments}
                        isLoadingMore={isLoadingMoreComments}
                        isLoadMore={isLoadMoreComments}
                        loadMore={loadMoreComments}
                        activeFormReply={null}
                        // TODO: fix type when refactoring CommentsList to use TS
                        setActiveFormReply={handleReply as any}
                        mutateComments={mutateComments}
                        markAllAsReadState={{
                            markRepliesAsRead: false,
                            isSubmitting: false,
                            error: null
                        }}
                        setMarkAllAsRead={() => {}}
                        handleMarkAllAsRead={() => {}}
                        variant="socialInbox"
                    />
                )}
            </div>

            <SocialInboxForm onSubmit={scrollToBottom} />
        </FormProvider>
    );
};
